import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';

import { Button } from 'antd';

import DisplayDuration from '@src/components/DisplayDuration';
import EllipsisWithTooltip from '@src/components/EllipsisWithTooltip/EllipsisWithTooltip';
import TableProgress from '@src/components/Table/components/TableProgress';
import type { ResponsiveTableColumn } from '@src/components/Table';
import { CommonReduxState } from '@src/models/commons/reduxState.interface';
import { getUserTimeZoneDate } from '@src/packages/utils';
import { getDurationFromStartDate } from '@src/packages/utils/functions/schedule';
import IntlMessages from '@src/util/IntlMessages';

import styles from '../../SchedulesDashboard/styles.module.less';
import { useCheckpointRoutesDashboardContext } from '../context/CheckpointRoutesDashboardContext';
import type { ScheduleDashboardSchedule, ScheduleMembers, TableDataRow } from '../types';

interface NestedSchedulePayload {
  startAt?: { _seconds: number; _nanoseconds: number };
  timeZone?: string;
}

interface ScheduleWithNested extends ScheduleDashboardSchedule {
  schedule?: NestedSchedulePayload;
}

const useCheckpointRoutesDashboardColumns = (): ResponsiveTableColumn<TableDataRow>[] => {
  const profile = useSelector(({ user }: CommonReduxState) => user.profile);
  const { locale = 'es' } = useSelector(({ settings }: CommonReduxState) => settings.locale);

  const { groupBy, goToTimeline, setSelectedUsers, toggleDrawer } =
    useCheckpointRoutesDashboardContext();

  const intl = useIntl();

  return [
    {
      key: 'schedule',
      title: intl.formatMessage({ id: 'checkpoint.route.title' }),
      dataIndex: 'schedule',
      align: 'left',
      width: 150,
      render: (record: TableDataRow) => (
        <EllipsisWithTooltip text={record.schedule?.name} />
      ),
    },
    {
      key: 'division',
      title: intl.formatMessage({ id: 'schedules.list.division.label' }),
      dataIndex: 'schedule',
      align: 'left',
      ellipsis: true,
      width: 150,
      render: (record: TableDataRow) => (
        <EllipsisWithTooltip text={record.schedule?.divisionName || '-'} />
      ),
    },
    {
      key: 'duration',
      title: intl.formatMessage({ id: 'schedules.list.duration.label' }),
      dataIndex: 'schedule',
      align: 'center',
      render: (record: TableDataRow) => (
        <DisplayDuration minutes={record?.schedule?.duration || 0} />
      ),
    },
    {
      key: 'duration_time',
      title: intl.formatMessage({ id: `schedules.list.time.${locale}.label` }),
      dataIndex: 'duration',
      align: 'center',
      render: (record: TableDataRow) => {
        const scheduleWithNested = record.schedule as ScheduleWithNested | undefined;
        const { schedule, duration } = scheduleWithNested || {};
        const { startAt, timeZone: scheduleTimeZone } = schedule || {};

        if (!duration || !startAt) return '-';

        const toMillis = (ts: { _seconds: number; _nanoseconds: number } | null | undefined) =>
          ts ? ts._seconds * 1000 + ts._nanoseconds / 1_000_000 : null;

        const startAtMillis = toMillis(startAt);
        const dateWithTimeZone = getUserTimeZoneDate(
          startAtMillis,
          scheduleTimeZone,
          profile?.data?.timeZone,
        ).jsDate;

        return (
          <IntlMessages
            id={`schedules.list.time.${locale}.value`}
            values={{
              startDate: dateWithTimeZone,
              endDate: getDurationFromStartDate(dateWithTimeZone, duration),
            }}
          />
        );
      },
    },
    {
      key: 'members',
      title: intl.formatMessage({ id: 'schedules.list.users.label' }),
      dataIndex: 'schedule',
      align: 'center',
      render: (record: TableDataRow) => {
        const members: ScheduleMembers = record.schedule?.members || {};
        const users = members.users || [];
        const groups = members.groups || [];
        const totalUsers = users.length + groups.length;

        if (totalUsers === 0) return '-';

        if (totalUsers === 1) {
          const singleItem = users[0] || groups[0];
          return `${singleItem?.firstName || ''} ${singleItem?.lastName || ''}`.trim();
        }

        return (
          <Button
            type="link"
            className="gx-mb-0 gx-px-0"
            onClick={() => {
              setSelectedUsers(members);
              toggleDrawer('users');
            }}
          >
            {totalUsers} {intl.formatMessage({ id: 'general.users' })}
          </Button>
        );
      },
    },
    {
      key: 'events',
      title: intl.formatMessage({ id: 'schedules.list.total.events' }),
      dataIndex: 'aggregation',
      align: 'center',
      render: (record: TableDataRow) => {
        const { finishedExecutions, expectedExecutions } = record.scheduleData;
        return (
          <Button
            type="link"
            className="gx-mb-0 gx-px-0"
            disabled={finishedExecutions === 0}
            onClick={() =>
              finishedExecutions > 0 &&
              goToTimeline({
                divisionId: record.schedule.divId,
                scheduleId: record.schedule.id,
                triggerID: null,
                dateKeyArg: record.dateKey,
                groupByArg: groupBy,
                finishedExecutions,
                expectedExecutions,
              })
            }
          >
            {finishedExecutions}/{expectedExecutions}
          </Button>
        );
      },
    },
    {
      key: 'progress',
      title: intl.formatMessage({ id: 'schedules.list.progress.label' }),
      dataIndex: 'scheduleData',
      align: 'center',
      render: (record: TableDataRow) => (
        <div className={styles.progressContainer} style={{ minWidth: '120px' }}>
          <TableProgress percentage={record.scheduleData.finishedPercentage} />
        </div>
      ),
    },
  ];
};

export default useCheckpointRoutesDashboardColumns;
